import type { Notification } from "@/types/database";

type Role = string | null | undefined;

/**
 * Resolve a notification to an in-app route based on the recipient's role.
 *
 * Notifications already carry the id of the thing they're about in `data`
 * (e.g. student_id), so we route by `type` and, where useful, deep-link to a
 * specific record. Returns `null` for purely informational notifications with
 * no sensible destination — in that case the bell just marks it read.
 *
 * Routing is role-aware: a student lands on their own pages (`/student/*`),
 * while admin/owner/staff land in the operational area (`/admin/*`).
 */
export function notificationRoute(n: Notification, role: Role): string | null {
  const data = (n.data ?? {}) as Record<string, unknown>;
  const studentId =
    typeof data.student_id === "string" ? data.student_id : null;
  const t = n.type ?? "";

  if (role === "student") {
    if (t.startsWith("maintenance")) return "/student/maintenance";
    if (t.startsWith("transport")) return "/student/transport";
    // Extension/renewal offers surface as a card on the student home.
    if (t === "contract_extension_offer") return "/student/home";
    if (t.startsWith("contract")) return "/student/contract";
    if (t.startsWith("renewal")) return "/student/home";
    if (t.startsWith("cancellation") || t.startsWith("move_out"))
      return "/student/move-out";
    if (t.startsWith("application") || t.startsWith("registration"))
      return "/student/home";
    return null;
  }

  // admin / owner / staff — operational area
  if (t.startsWith("maintenance")) return "/admin/maintenance";
  if (t.startsWith("transport")) return "/admin/transport";
  if (t.startsWith("contract")) return "/admin/contracts";
  if (t.startsWith("renewal")) return "/admin/renewals";
  if (t === "move_out_notice" || t.startsWith("cancellation"))
    return studentId ? `/admin/students/${studentId}` : "/admin/students";
  if (
    t.startsWith("application") ||
    t.startsWith("registration") ||
    t.startsWith("approval")
  )
    return "/admin/approvals";
  return null;
}
